import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, apiClient } from '../services/api';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Define isAuthenticated before using it in useEffect
  const isAuthenticated = !!user && !!accessToken;

  // Configurar callbacks da API
  useEffect(() => {
    apiClient.setCallbacks({
      onTokenRefresh: (newAccessToken: string, newRefreshToken: string, newUser?: { id: string; email: string; name: string; role: string; tenantId: string | null }) => {
        logger.log('[AuthContext] Tokens renovados automaticamente');
        // Atualizar tokens no estado e localStorage
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Atualizar user se veio dados frescos do backend (role pode ter mudado)
        if (newUser) {
          const updatedUser = { ...user, ...newUser } as User;
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
          logger.log(`[AuthContext] User atualizado: role=${newUser.role}`);
        }
      },
      onAuthError: () => {
        logger.log('[AuthContext] Erro de autenticação - limpando sessão e redirecionando para login');

        // Limpar tudo quando autenticação falhar definitivamente
        setUser(null);
        setAccessToken(null);
        setRefreshToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        // Redirecionar para login se não estiver já na página de login
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
      },
    });
  }, [navigate, location]);

  // Função auxiliar para validar formato JWT
  const isValidJWT = (token: string): boolean => {
    if (!token) return false;
    const parts = token.split('.');
    // JWT deve ter 3 partes: header.payload.signature
    if (parts.length !== 3) return false;
    // Verificar se as partes são base64 válidas
    try {
      atob(parts[0]);
      atob(parts[1]);
      return true;
    } catch {
      return false;
    }
  };

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');

    if (storedUser && storedAccessToken && storedRefreshToken) {
      // Validar formato do JWT antes de usar
      if (!isValidJWT(storedAccessToken) || !isValidJWT(storedRefreshToken)) {
        // Token inválido, limpar storage
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } else {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setAccessToken(storedAccessToken);
        setRefreshToken(storedRefreshToken);

        // Validar dados do user contra o backend (role pode ter mudado)
        apiClient.getProfile(storedAccessToken).then(response => {
          if (response.success && response.data?.user) {
            const freshUser = response.data.user;
            if (freshUser.role !== parsedUser.role || freshUser.tenantId !== parsedUser.tenantId) {
              logger.log(`[AuthContext] Role mudou: ${parsedUser.role} → ${freshUser.role}`);
              const updatedUser = { ...parsedUser, ...freshUser };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          }
        }).catch(() => {
          // Token expirado ou inválido — o interceptor de 401 cuida do refresh/logout
        });
      }
    }
    setIsLoading(false);
  }, []);

  const login = (user: User, accessToken: string, refreshToken: string) => {
    setUser(user);
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);

    // Persist to localStorage
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // SEMPRE redirecionar baseado no role do usuário
    if (user.role === 'super_admin') {
      navigate('/admin', { replace: true });
    } else if (user.role === 'admin') {
      navigate('/admin/dashboard', { replace: true });
    } else {
      navigate('/waba', { replace: true });
    }
  };

  const logout = () => {
    logger.log('[AuthContext] Logging out...');

    // Clear state
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);

    // Clear localStorage - garantir que tudo seja removido
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      logger.log('[AuthContext] LocalStorage cleared successfully');
    } catch (error) {
      logger.error('[AuthContext] Error clearing localStorage:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        refreshToken,
        login,
        logout,
        isAuthenticated,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
